import React, { PureComponent } from "react";

import { Field, reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";

import { Input, Grid, Col, CheckMark, Dropdown, Datepicker } from "../../components";
import { messages, constants } from "../../constants";

import { validationBuilder, validations } from "../../containers/form";
import { Txt, Button, Title } from "../../elements";

import { convertToSentenceCase, validateEmail } from "../../util";

import moment from "moment-timezone";

import partnershipInquiryImage from "../../assets/img/partnership-inquiry.png";
import "./index.scss";
import { EmailFormData } from "../../util/emailFormData";
import RequiredFieldFooterNote from "../../containers/form/fieldGroups/requiredFieldFooterNote";

let rules = {};

const optional = (rule) => (value) => {
  if (!value) {
    return undefined;
  }
  return rule(value);
};

const settings = {
  form: constants.FORM.partnershipInquiryForm, // <------ form name
  destroyOnUnmount: false, // <------ preserve form data
  forceUnregisterOnUnmount: true // <------ unregister fields on unmount
};

class PartnershipInquiry extends PureComponent {
  rulePhone = [];
  state = {
    clientSideValidationError: null,
    resetSuccess: false
  };

  constructor(props) {
    super(props);
    // for field level validations
    // TODO: find a better way to instantiate props when the component mounts
    Object.keys(validations).forEach((key) => {
      rules[key] = (spec) => (value) => validationBuilder(props, validations[key], spec)(value);
    });
    const { intl } = props;
    this.ruleFirstName = [
      rules.required(intl.formatMessage(messages.personal.firstName)),
      rules.names(intl.formatMessage(messages.personal.firstName))
    ];
    this.ruleLastName = [
      rules.required(intl.formatMessage(messages.personal.lastName)),
      rules.names(intl.formatMessage(messages.personal.lastName))
    ];
    this.rulesEmail = [
      rules.required(intl.formatMessage(messages.field.EmailAddress)),
      rules.email()
    ];
    this.rulePhone = [rules.phone()];
    this.ruleRequestType = [
      rules.required(intl.formatMessage(messages.partnershipInquiry.requestType))
    ];
    this.ruleRequestDetails = [
      rules.required(intl.formatMessage(messages.partnershipInquiry.requestDetails))
    ];
    this.ruleEventDate = [
      optional(rules.isValidDate(intl.formatMessage(messages.error.invalidDate))),
      optional(rules.isNotPastDay(intl.formatMessage(messages.error.isFuture)))
    ];
  }

  sendEmail = async (payload) => {
    const { intl } = this.props;
    if (!validateEmail(payload.email)) {
      this.setState({ clientSideValidationError: intl.formatMessage(messages.error.email) });
      return null;
    }
    const emailFormData = new EmailFormData(payload, constants.EMAIL_TYPE.PARTNERSHIP_INQUIRY)

    try {
      const resp = await fetch(constants.ENDPOINTS.sendEmail, {
        method: "POST",
        body: emailFormData.getFormData()
      });

      if (resp.status !== 200) {
        this.setState({
          clientSideValidationError: "Something went wrong!",
          resetSuccess: false
        });
      } else {
        this.setState({
          clientSideValidationError: null,
          resetSuccess: true
        });
      }
      return resp;
    } catch (error) {
      this.setState({
        clientSideValidationError: error.message,
        resetSuccess: false
      });
      //   throw error;
    }
  };

  renderSuccessfulScreen = () => {
    const { intl } = this.props;

    return (
      <div className="reset-code" data-testid="partnership-inquiry-success" id="partnership-inquiry-success">
        <Grid theme={["center"]}>
          <Col size="1-2" min="sm">
            <CheckMark />
            <Title
              priority={1}
              type={["strong"]}
              style={{
                padding: "50px"
              }}
              className="h1 reset-code__title blue"
            >
              {intl.formatMessage(messages.partnershipInquiry.successMessage)}
            </Title>
            <Title priority={5} className="h5 reset-code__title">
              {intl.formatMessage(messages.partnershipInquiry.successMessageDescription)}
            </Title>
          </Col>
        </Grid>
      </div>
    );
  };

  render() {
    const { submitting, invalid, pristine, intl, handleSubmit } = this.props;

    if (this.state.clientSideValidationError) {
      return (
        <div data-testid="partnership-inquiry-error" id="partnership-inquiry-error" style={{ display: "contents" }}>
          {this.state.clientSideValidationError}
        </div>
      );
    }

    if (this.state.resetSuccess) {
      return this.renderSuccessfulScreen();
    }
    return (
      <div className="outerForm">
        <img
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          src={partnershipInquiryImage}
          alt="EvoApp"
        />
        <div style={{ padding: "30px 0" }}>
          <form
            model={constants.FORM.partnershipInquiryForm}
            onSubmit={handleSubmit(this.sendEmail)}
            noValidate
          >
            <Title priority={1} type={["strong-no-cap"]} style={{ fontSize: "1.5rem" }}>
              {convertToSentenceCase(intl.formatMessage(messages.partnershipInquiry.title))}
            </Title>
            <Txt theme={[`middle`, `large`]}>
              {intl.formatMessage(messages.partnershipInquiry.description)}
            </Txt>
            <Grid
              className={"partnership-form"}
              style={{ padding: "0", margin: "0", gridGap: "20px" }}
            >
              <Col size="1-2" min="sm">
                <Field
                  type="text"
                  name="firstName"
                  label={intl.formatMessage(messages.partnershipInquiry.firstName)}
                  required={true}
                  component={Input}
                  validate={this.ruleFirstName}
                  props={{ maxLength: "50" }}
                />
              </Col>
              <Col size="1-2" min="sm">
                <Field
                  type="text"
                  name="lastName"
                  label={intl.formatMessage(messages.partnershipInquiry.lastName)}
                  required={true}
                  component={Input}
                  validate={this.ruleLastName}
                  props={{ maxLength: "50" }}
                />
              </Col>
              <Col size="2-4" min="sm">
                <Field
                  type="email"
                  name="email"
                  label={intl.formatMessage(messages.partnershipInquiry.email)}
                  required={true}
                  validate={this.rulesEmail}
                  component={Input}
                  props={{ maxLength: "50" }}
                />
              </Col>
              <Col size="2-3" min="sm">
                <Field
                  type="tel"
                  name="phoneNumber"
                  label={intl.formatMessage(messages.partnershipInquiry.phone)}
                  component={Input}
                  validate={this.rulePhone}
                  required={true}
                  props={{ maxLength: "20" }}
                />
              </Col>
              <Col size="1-1" min="sm">
                <Field
                  type="text"
                  name="organization"
                  label={intl.formatMessage(messages.partnershipInquiry.organization)}
                  component={Input}
                  props={{ maxLength: "100" }}
                />
              </Col>
              <Col size="1-2" min="sm">
                <Field
                  name="eventDate"
                  label={intl.formatMessage(messages.partnershipInquiry.eventDate)}
                  component={Datepicker}
                  validate={this.ruleEventDate}
                  isOptional={true}
                  disabledDays={{ before: moment().startOf("day").toDate() }}
                  initialMonth={moment().toDate()}
                />
                <Txt>
                  <small>{intl.formatMessage(messages.partnershipInquiry.eventDateSubtext)}</small>
                </Txt>
              </Col>
              <Col size="1-1" min="sm">
                <Field
                  name="requestType"
                  label={intl.formatMessage(messages.partnershipInquiry.requestType)}
                  required={true}
                  validate={this.ruleRequestType}
                  options={[
                    { label: intl.formatMessage(messages.partnershipInquiry.requestTypeEvent), value: "Event" },
                    { label: intl.formatMessage(messages.partnershipInquiry.requestTypeSponsorship), value: "Sponsorship" },
                    { label: intl.formatMessage(messages.partnershipInquiry.requestTypeDonation), value: "Donation" },
                    { label: intl.formatMessage(messages.partnershipInquiry.requestTypeSocialCollab), value: "Social Collab" },
                    { label: intl.formatMessage(messages.partnershipInquiry.requestTypeOther), value: "Other" }
                  ]}
                  component={Dropdown}
                />
              </Col>
              <Col size="1-1" min="sm">
                <Field
                  type="text"
                  name="requestDetails"
                  label={intl.formatMessage(messages.partnershipInquiry.requestDetails)}
                  required={true}
                  component={Input}
                  validate={this.ruleRequestDetails}
                  props={{ maxLength: "500" }}
                />
              </Col>
              <Col size="3-4" min="sm">
                <RequiredFieldFooterNote />
              </Col>
            </Grid>

            <br />
            <Button
              loading={submitting}
              type="submit"
              theme={["cta"]}
              disabled={invalid || submitting || pristine}
            >
              {intl.formatMessage(messages.button.submit)}
            </Button>
          </form>
        </div>
      </div>
    );
  }
}

PartnershipInquiry.propTypes = {
  intl: PropTypes.object.isRequired,
  submitting: PropTypes.bool
};

PartnershipInquiry.defaultProps = {
  button: "",
  submitting: false
};

const mapStateToProps = (state) => {
  const selector = formValueSelector(constants.FORM.partnershipInquiryForm);

  return {
    state,
    selectedRequestType: selector(state, "requestType")
  };
};

const enhancer = compose(injectIntl, connect(mapStateToProps), reduxForm(settings));

export default enhancer(PartnershipInquiry);
